import { CloudArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { addMonths, format } from "date-fns";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	DescriptionDetails,
	DescriptionList,
	DescriptionTerm,
} from "@/components/catalyst/DescriptionList";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Heading } from "@/components/catalyst/Heading";
import { Input } from "@/components/catalyst/Input";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";

export function OfferCodesTab() {
	const { linkId } = useParams({
		from: "/_app/apps/$appId/links/$linkId/",
	});

	const [pendingFile, setPendingFile] = useState<File | null>(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [expiryDate, setExpiryDate] = useState(() =>
		format(addMonths(new Date(), 3), "yyyy-MM-dd"),
	);

	const { data: link } = useSuspenseQuery(
		orpc.referralLinks.detail.queryOptions({
			input: { id: linkId },
		}),
	);

	const statsQuery = useSuspenseQuery(
		orpc.offerCodes.stats.queryOptions({
			input: { referralLinkId: linkId },
		}),
	);

	const uploadMutation = useMutation(
		orpc.offerCodes.upload.mutationOptions({
			onSuccess: (data) => {
				toast.success(`Upload started: ${data.fileName}`);
				setIsDialogOpen(false);
				setPendingFile(null);
				statsQuery.refetch();
			},
			onError: (error) => {
				toast.error(error.message || "Upload failed");
			},
		}),
	);

	const deleteMutation = useMutation(
		orpc.offerCodes.deleteUnclaimed.mutationOptions({
			onSuccess: (data) => {
				toast.success(`Deleted ${data.deletedCount} unclaimed codes`);
				setIsDeleteDialogOpen(false);
				statsQuery.refetch();
			},
			onError: (error) => {
				toast.error(error.message || "Delete failed");
			},
		}),
	);

	const handleFileSelect = (file: File) => {
		setPendingFile(file);
		setIsDialogOpen(true);
	};

	const handleUploadConfirm = () => {
		if (!pendingFile) return;

		uploadMutation.mutate({
			referralLinkId: linkId,
			expiresAt: new Date(expiryDate),
			file: pendingFile,
		});
	};

	const handleDialogClose = () => {
		if (uploadMutation.isPending) return;
		setIsDialogOpen(false);
		setPendingFile(null);
	};

	const handleDeleteConfirm = () => {
		deleteMutation.mutate({ referralLinkId: linkId });
	};

	const stats = statsQuery.data;
	const hasAnyCodes = stats.totalCodes > 0;
	const showStats = hasAnyCodes || stats.isProcessing;

	return (
		<>
			{/* Upload Dialog */}
			<Dialog open={isDialogOpen} onClose={handleDialogClose}>
				<DialogTitle>Upload Offer Codes</DialogTitle>
				<DialogDescription>
					Configure settings for the codes in{" "}
					<span className="font-medium">{pendingFile?.name}</span>
				</DialogDescription>
				<DialogBody className="space-y-4">
					<Field>
						<Label>Expiry Date</Label>
						<Input
							type="date"
							value={expiryDate}
							onChange={(e) => setExpiryDate(e.target.value)}
							min={format(new Date(), "yyyy-MM-dd")}
						/>
						<Text className="text-xs mt-1">
							When these offer codes will expire
						</Text>
					</Field>

					<Text>
						This link is for{" "}
						<strong className="text-base-content font-medium capitalize">
							{link.planHint}
						</strong>{" "}
						plans. Please ensure the codes you are uploading match this
						duration.
					</Text>
				</DialogBody>
				<DialogActions>
					<Button
						plain
						onClick={handleDialogClose}
						disabled={uploadMutation.isPending}
					>
						Cancel
					</Button>
					<Button
						onClick={handleUploadConfirm}
						disabled={uploadMutation.isPending}
					>
						{uploadMutation.isPending ? "Uploading..." : "Upload"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
			>
				<DialogTitle>Delete Unclaimed Codes</DialogTitle>
				<DialogDescription>
					Are you sure you want to delete all {stats.availableCodes} unclaimed
					offer codes? This action cannot be undone.
				</DialogDescription>
				<DialogActions>
					<Button
						plain
						onClick={() => setIsDeleteDialogOpen(false)}
						disabled={deleteMutation.isPending}
					>
						Cancel
					</Button>
					<Button
						color="red"
						onClick={handleDeleteConfirm}
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Stats Section */}
			{showStats && (
				<div className="mb-6 rounded-lg border border-base-content/10 p-4">
					<div className="flex items-center justify-between mb-3">
						<div className="flex items-center gap-2">
							<Heading level={3} className="text-sm font-medium">
								Offer Code Statistics
							</Heading>
							{stats.isProcessing && (
								<span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
									<span className="size-1.5 rounded-full bg-warning animate-pulse" />
									Processing
								</span>
							)}
						</div>
						{stats.availableCodes > 0 && (
							<Button
								plain
								onClick={() => setIsDeleteDialogOpen(true)}
								className="text-error text-xs"
							>
								<TrashIcon className="size-4 mr-1" />
								Delete Unclaimed
							</Button>
						)}
					</div>
					<DescriptionList>
						<DescriptionTerm>Total Uploaded</DescriptionTerm>
						<DescriptionDetails>
							{stats.totalCodes}
							{stats.isProcessing && (
								<span className="text-base-content/50 ml-1">
									(still importing...)
								</span>
							)}
						</DescriptionDetails>

						<DescriptionTerm>Available</DescriptionTerm>
						<DescriptionDetails className="text-success">
							{stats.availableCodes}
						</DescriptionDetails>

						<DescriptionTerm>Redeemed</DescriptionTerm>
						<DescriptionDetails>{stats.redeemedCodes}</DescriptionDetails>
					</DescriptionList>
				</div>
			)}

			{/* Single Dropzone - platform is auto-detected from CSV format */}
			<Dropzone
				label="Upload Offer Codes"
				description={`This referral link is configured for ${
					link.planHint === "monthly" ? "Monthly" : "Yearly"
				} subscriptions. Please ensure your uploaded codes match.`}
				accept=".csv,text/csv"
				onFileSelect={handleFileSelect}
			/>
		</>
	);
}

function Dropzone({
	label,
	description,
	accept,
	onFileSelect,
}: {
	label: string;
	description: string;
	accept: string;
	onFileSelect: (file: File) => void;
}) {
	const [isDragActive, setIsDragActive] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragActive(true);
	};

	const handleDragLeave = () => {
		setIsDragActive(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragActive(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length === 0) return;

		const file = files[0];
		if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
			toast.error("Only CSV files are allowed");
			return;
		}

		onFileSelect(file);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			onFileSelect(e.target.files[0]);
		}
	};

	const triggerFileSelect = () => {
		inputRef.current?.click();
	};

	return (
		<section
			className={`relative flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-12 transition-colors ${
				isDragActive
					? "border-primary bg-primary/5"
					: "border-base-content/10 hover:border-base-content/20"
			}`}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			aria-label="Drop files to upload"
		>
			<div className="rounded-full bg-base-content/5 p-4">
				<CloudArrowUpIcon className="size-8 text-base-content" />
			</div>
			<div className="text-center">
				<Heading level={3} className="text-sm font-medium">
					{label}
				</Heading>
				<Text className="mt-1 text-xs max-w-sm">{description}</Text>
				<div className="mt-3 flex items-center justify-center gap-4">
					<a
						href="https://developer.apple.com/help/app-store-connect/manage-subscriptions/set-up-subscription-offer-codes/"
						target="_blank"
						rel="noreferrer"
						className="text-xs text-primary hover:underline cursor-pointer"
						onClick={(e) => e.stopPropagation()}
					>
						iOS Docs
					</a>
					<span className="text-base-content/30">|</span>
					<a
						href="https://support.google.com/googleplay/android-developer/answer/6321495"
						target="_blank"
						rel="noreferrer"
						className="text-xs text-primary hover:underline cursor-pointer"
						onClick={(e) => e.stopPropagation()}
					>
						Android Docs
					</a>
				</div>
			</div>

			<div className="relative">
				<Button type="button" onClick={triggerFileSelect}>
					Select CSV File
				</Button>
				<input
					ref={inputRef}
					type="file"
					accept={accept}
					className="hidden"
					onChange={handleInputChange}
				/>
			</div>
		</section>
	);
}
